/**
 * Metrics-section frame (§5.1 #24, §8.10; redesign § Metrics mode).
 *
 * Every section arrives as `{state, reason?, data, tooltip}`. This component paints the
 * discriminant: `ok` renders the caller's island(s), `empty` renders the server's `reason`
 * verbatim — the client never decides *why* a section is empty (§12.5 rule 5). Per the redesign,
 * the header (title, section tooltip, actions) sits **outside** the island; children own their
 * `.card` markup so a section can render sibling islands (e.g. the per-member table).
 */

import type { ReactNode } from 'react';
import type { Section } from '@lw/shared';
import { EmptyState, TooltipPopover } from '../../../components';

export type SectionCardProps<T> = {
  title: string;
  section: Section<T> | undefined;
  /** Rendered in the section head, right of the title (outside the island). */
  actions?: ReactNode;
  /** Renders the island(s) — the callback provides its own `.card` wrapper(s). */
  children: (data: T) => ReactNode;
};

export function SectionCard<T>({ title, section, actions, children }: SectionCardProps<T>) {
  if (section === undefined) return null;

  return (
    <section className="section">
      <div className="section-head">
        <h2 className="section-title metrics-title">
          {title}
          <TooltipPopover tooltip={section.tooltip} label={`How ${title} is calculated`} />
        </h2>
        {actions !== undefined ? <div className="section-head__actions">{actions}</div> : null}
      </div>

      {section.state === 'empty' ? (
        <div className="card">
          <EmptyState title="Not enough data yet" description={section.reason} />
        </div>
      ) : (
        children(section.data)
      )}
    </section>
  );
}

export default SectionCard;
